import { PageHeader } from "@/components/feature/PageHeader";
import { SettingsNav } from "../components/SettingsNav";
import { Card } from "@/components/base/Card";
import { Alert } from "@/components/base/Alert";
import { cn } from "@/utils/cn";
import type { Role } from "@/services/types";
import { ROLES, roleLabel } from "@/config/roles";

type Cap = "view" | "create" | "edit" | "approve" | "delete";

const CAP_LABEL: Record<Cap, string> = {
  view: "View",
  create: "Create",
  edit: "Edit",
  approve: "Approve",
  delete: "Delete",
};

interface ModulePerm {
  module: string;
  note: string;
  perms: Record<Role, Cap[]>;
}

/** Fixed by role in this release — a reference, not configurable. */
const MATRIX: ModulePerm[] = [
  {
    module: "Member master",
    note: "Customer records, profile, documents",
    perms: {
      super_admin: ["view", "create", "edit", "delete"],
      admin: ["view", "create", "edit", "delete"],
      accountant: ["view"],
      collection_agent: ["view"],
      viewer: ["view"],
    },
  },
  {
    module: "Groups & enrolment",
    note: "Create groups, enrol members, activate",
    perms: {
      super_admin: ["view", "create", "edit", "approve"],
      admin: ["view", "create", "edit", "approve"],
      accountant: ["view"],
      collection_agent: ["view"],
      viewer: ["view"],
    },
  },
  {
    module: "Auction console",
    note: "Record bids, withdraw, close auctions",
    perms: {
      super_admin: ["view", "create", "edit", "approve"],
      admin: ["view", "create", "edit", "approve"],
      accountant: ["view", "create", "edit", "approve"],
      collection_agent: ["view", "create", "edit", "approve"],
      viewer: ["view"],
    },
  },
  {
    module: "Cash collection",
    note: "Record receipts, reverse, late fees",
    perms: {
      super_admin: ["view", "create", "delete"],
      admin: ["view", "create", "delete"],
      accountant: ["view", "create", "delete"],
      collection_agent: ["view", "create"],
      viewer: ["view"],
    },
  },
  {
    module: "Payout",
    note: "Approve, disburse, reverse",
    perms: {
      super_admin: ["view", "create", "edit", "approve", "delete"],
      admin: ["view", "create", "edit", "approve", "delete"],
      accountant: ["view", "create", "edit"],
      collection_agent: ["view"],
      viewer: ["view"],
    },
  },
  {
    module: "Reports & ledger",
    note: "Statements, registers, ledger adjustments",
    perms: {
      super_admin: ["view", "edit"],
      admin: ["view", "edit"],
      accountant: ["view", "edit"],
      collection_agent: ["view"],
      viewer: ["view"],
    },
  },
  {
    module: "Business settings",
    note: "Business profile & defaults",
    perms: {
      super_admin: ["view", "edit"],
      admin: ["view", "edit"],
      accountant: ["view"],
      collection_agent: ["view"],
      viewer: ["view"],
    },
  },
  {
    module: "Staff users",
    note: "Create, edit, deactivate, reset passwords",
    perms: {
      super_admin: ["view", "create", "edit", "delete"],
      admin: ["view"],
      accountant: ["view"],
      collection_agent: ["view"],
      viewer: ["view"],
    },
  },
  {
    module: "IP approvals",
    note: "Authorize staff IP addresses & system access",
    perms: {
      super_admin: ["view", "edit", "approve", "delete"],
      admin: ["view", "edit", "approve", "delete"],
      accountant: ["view"],
      collection_agent: ["view"],
      viewer: ["view"],
    },
  },
  {
    module: "Document numbering",
    note: "Prefixes & sequences",
    perms: {
      super_admin: ["view", "edit"],
      admin: ["view"],
      accountant: ["view"],
      collection_agent: ["view"],
      viewer: ["view"],
    },
  },
  {
    module: "Audit log",
    note: "Read-only trail with export",
    perms: {
      super_admin: ["view"],
      admin: ["view"],
      accountant: ["view"],
      collection_agent: ["view"],
      viewer: ["view"],
    },
  },
];

const ROLE_ORDER: Role[] = ["super_admin", "admin", "accountant", "collection_agent", "viewer"];

const CAP_CHIP: Record<Cap, string> = {
  view: "bg-secondary-100 text-secondary-800",
  create: "bg-primary-100 text-primary-800",
  edit: "bg-info-100 text-info-800",
  approve: "bg-accent-100 text-accent-800",
  delete: "bg-danger-100 text-danger-700",
};

export default function PermissionsSettingsPage() {
  return (
    <div>
      <PageHeader
        title="Settings"
        subtitle="Organization configuration, staff users and the audit trail."
      />
      <SettingsNav />

      <div className="mb-3">
        <Alert variant="info" title="Reference only">
          Permissions are fixed by role in this release. This matrix shows exactly what each role can do so you can
          decide what to assign before creating an account.
        </Alert>
      </div>

      <Card padding={false}>
        <div className="overflow-auto">
          <table className="w-full min-w-[820px] border-collapse text-sm">
            <thead>
              <tr className="bg-background-100/80 text-left">
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-foreground-500">Module</th>
                {ROLE_ORDER.map((r) => (
                  <th key={r} className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wide text-foreground-500">
                    {roleLabel(r)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MATRIX.map((m) => (
                <tr key={m.module} className="border-t border-background-100 align-top">
                  <td className="px-4 py-3">
                    <p className="font-medium text-foreground-900">{m.module}</p>
                    <p className="text-xs text-foreground-500">{m.note}</p>
                  </td>
                  {ROLE_ORDER.map((r) => {
                    const caps = m.perms[r];
                    return (
                      <td key={r} className="px-3 py-3 text-center">
                        <div className="flex flex-wrap items-center justify-center gap-1">
                          {caps.length === 0 ? (
                            <span className="text-foreground-300">—</span>
                          ) : (
                            caps.map((c) => (
                              <span
                                key={c}
                                title={CAP_LABEL[c]}
                                className={cn("inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium", CAP_CHIP[c])}
                              >
                                {CAP_LABEL[c].charAt(0)}
                              </span>
                            ))
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap items-center gap-4 border-t border-background-100 px-4 py-3 text-xs text-foreground-600">
          {(["view", "create", "edit", "approve", "delete"] as Cap[]).map((c) => (
            <span key={c} className="inline-flex items-center gap-1.5">
              <span className={cn("inline-flex h-5 w-5 items-center justify-center rounded text-[11px] font-medium", CAP_CHIP[c])}>
                {CAP_LABEL[c].charAt(0)}
              </span>
              {CAP_LABEL[c]}
            </span>
          ))}
          <span className="ml-auto text-foreground-400">{ROLES.length} roles fixed in this release</span>
        </div>
      </Card>
    </div>
  );
}